// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Alert, Spinner } from '@loa/ui-components';
import {
  financialPositionApi,
  type AnticipatedInheritance,
  type CatGroup,
  type CorporationTax,
} from '../../api/financialPositionApi';

interface Props {
  profileId: string;
}

/** CAT group select options + friendly labels (cashflow-tax-modeling-prd.md §7.3). */
const CAT_GROUPS: Array<{ value: CatGroup; label: string }> = [
  { value: 'A', label: 'Group A — child (€400k)' },
  { value: 'B', label: 'Group B — close relative (€40k)' },
  { value: 'C', label: 'Group C — other (€20k)' },
];

const CAT_GROUP_LABEL: Record<CatGroup, string> = {
  A: CAT_GROUPS[0].label,
  B: CAT_GROUPS[1].label,
  C: CAT_GROUPS[2].label,
};

const RELIEF_FIELDS: Array<{
  key: 'isBusinessAsset' | 'isAgricultural' | 'smallGiftApplicable';
  label: string;
}> = [
  { key: 'isBusinessAsset', label: 'Business asset' },
  { key: 'isAgricultural', label: 'Agricultural' },
  { key: 'smallGiftApplicable', label: 'Small gift' },
];

/** An empty draft for the "Add inheritance" form. */
function blankDraft(): AnticipatedInheritance {
  return {
    id: null,
    ownerClientId: null,
    sourceRelationship: null,
    catGroup: 'A',
    expectedValue: null,
    expectedYear: null,
    priorAggregateInGroup: 0,
    isBusinessAsset: false,
    isAgricultural: false,
    smallGiftApplicable: false,
  };
}

function reliefSummary(inh: AnticipatedInheritance): string {
  const reliefs = RELIEF_FIELDS.filter((f) => inh[f.key]).map((f) => f.label);
  return reliefs.length ? reliefs.join(', ') : '—';
}

function fmtMoney(value: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * §7.3 "Anticipated inheritances (CAT)" + §7.4 "Corporation tax" — advisor panel
 * to capture anticipated inheritances for Capital Acquisitions Tax planning, plus
 * a read-only display of corporation tax derived from captured company P&Ls.
 * Mounted on the relationship Financial Position tab beneath the tax-classification
 * panel. See docs/specs/cashflow-tax-modeling-prd.md §7.3/§7.4.
 */
export function InheritancesPanel({ profileId }: Props) {
  const [inheritances, setInheritances] = useState<AnticipatedInheritance[] | null>(null);
  const [corpTax, setCorpTax] = useState<CorporationTax[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    setError(null);
    return Promise.all([
      financialPositionApi.listInheritances(profileId),
      financialPositionApi.getCorporationTax(profileId).catch(() => [] as CorporationTax[]),
    ])
      .then(([inh, ct]) => {
        setInheritances(inh);
        setCorpTax(ct);
      })
      .catch(() => setError('Could not load anticipated inheritances.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      financialPositionApi.listInheritances(profileId),
      financialPositionApi.getCorporationTax(profileId).catch(() => [] as CorporationTax[]),
    ])
      .then(([inh, ct]) => {
        if (!cancelled) {
          setInheritances(inh);
          setCorpTax(ct);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load anticipated inheritances.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading) return <Spinner />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!inheritances) return null;

  return (
    <div data-testid="inheritances-panel" className="flex flex-col gap-lg">
      <InheritancesSection
        profileId={profileId}
        inheritances={inheritances}
        onChanged={reload}
      />
      <CorporationTaxSection rows={corpTax} />
    </div>
  );
}

function InheritancesSection({
  profileId,
  inheritances,
  onChanged,
}: {
  profileId: string;
  inheritances: AnticipatedInheritance[];
  onChanged: () => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="flex flex-col w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] p-lg gap-md">
      <div className="text-black text-body-01 font-medium leading-normal font-sans">
        Anticipated inheritances (CAT)
      </div>

      {inheritances.length === 0 ? (
        <div className="text-body-03 text-grey-04">
          No anticipated inheritances captured for this relationship yet.
        </div>
      ) : (
        <table className="w-full border-collapse text-body-03">
          <thead>
            <tr className="text-left text-grey-04">
              <th className="py-sm pr-sm font-medium">Relationship</th>
              <th className="py-sm px-sm font-medium">CAT group</th>
              <th className="py-sm px-sm font-medium">Expected value</th>
              <th className="py-sm px-sm font-medium">Expected year</th>
              <th className="py-sm px-sm font-medium">Reliefs</th>
              <th className="py-sm pl-sm font-medium" />
            </tr>
          </thead>
          <tbody>
            {inheritances.map((inh) =>
              editingId === inh.id ? (
                <InheritanceFormRow
                  key={inh.id}
                  profileId={profileId}
                  initial={inh}
                  onSaved={() => {
                    setEditingId(null);
                    onChanged();
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <InheritanceRow
                  key={inh.id}
                  profileId={profileId}
                  inheritance={inh}
                  onEdit={() => setEditingId(inh.id)}
                  onDeleted={onChanged}
                />
              ),
            )}
          </tbody>
        </table>
      )}

      <div className="border-t border-[#E1E1E1] pt-md">
        <div className="text-body-02 font-medium text-black mb-sm">Add inheritance</div>
        <InheritanceForm profileId={profileId} onSaved={onChanged} />
      </div>
    </div>
  );
}

function InheritanceRow({
  profileId,
  inheritance,
  onEdit,
  onDeleted,
}: {
  profileId: string;
  inheritance: AnticipatedInheritance;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = () => {
    if (busy || inheritance.id == null) return;
    setBusy(true);
    setError(null);
    financialPositionApi
      .deleteInheritance(profileId, inheritance.id)
      .then(() => onDeleted())
      .catch(() => {
        setError('Could not delete.');
        setBusy(false);
      });
  };

  return (
    <tr
      className="border-t border-[#E1E1E1] align-top"
      data-testid={`inheritance-row-${inheritance.id}`}
    >
      <td className="py-[10px] pr-sm text-black">{inheritance.sourceRelationship ?? '—'}</td>
      <td className="py-[10px] px-sm text-grey-04">{CAT_GROUP_LABEL[inheritance.catGroup]}</td>
      <td className="py-[10px] px-sm text-grey-04">{fmtMoney(inheritance.expectedValue)}</td>
      <td className="py-[10px] px-sm text-grey-04">{inheritance.expectedYear ?? '—'}</td>
      <td className="py-[10px] px-sm text-grey-04">{reliefSummary(inheritance)}</td>
      <td className="py-[10px] pl-sm">
        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            data-testid={`inheritance-edit-${inheritance.id}`}
            onClick={onEdit}
            disabled={busy}
            className="text-[13px] text-purple-primary hover:underline disabled:opacity-60"
          >
            Edit
          </button>
          <button
            type="button"
            data-testid={`inheritance-delete-${inheritance.id}`}
            onClick={remove}
            disabled={busy}
            className="text-[13px] text-[#C0392B] hover:underline disabled:opacity-60"
          >
            Delete
          </button>
          {error && <span className="text-body-04 text-[#C0392B]">{error}</span>}
        </div>
      </td>
    </tr>
  );
}

/** Inline edit form rendered as a table row (existing inheritance). */
function InheritanceFormRow({
  profileId,
  initial,
  onSaved,
  onCancel,
}: {
  profileId: string;
  initial: AnticipatedInheritance;
  onSaved: () => void;
  onCancel: () => void;
}) {
  return (
    <tr className="border-t border-[#E1E1E1]">
      <td colSpan={6} className="py-[12px]">
        <InheritanceForm
          profileId={profileId}
          initial={initial}
          onSaved={onSaved}
          onCancel={onCancel}
          testidPrefix={`inheritance-edit-form-${initial.id}`}
        />
      </td>
    </tr>
  );
}

const NUMBER_FIELDS: Array<{
  key: 'expectedValue' | 'expectedYear' | 'priorAggregateInGroup';
  label: string;
  integer?: boolean;
}> = [
  { key: 'expectedValue', label: 'Expected value' },
  { key: 'expectedYear', label: 'Expected year', integer: true },
  { key: 'priorAggregateInGroup', label: 'Prior aggregate in group' },
];

/**
 * Shared add/edit form. When {@code initial} is given it edits that row
 * (PUT); otherwise it creates a new one (POST). After a successful save the
 * create-form resets to a blank draft.
 */
function InheritanceForm({
  profileId,
  initial,
  onSaved,
  onCancel,
  testidPrefix = 'inheritance-new',
}: {
  profileId: string;
  initial?: AnticipatedInheritance;
  onSaved: () => void;
  onCancel?: () => void;
  testidPrefix?: string;
}) {
  const isEdit = initial != null && initial.id != null;
  const [form, setForm] = useState<AnticipatedInheritance>(initial ?? blankDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setNumber = (key: (typeof NUMBER_FIELDS)[number]['key'], raw: string, integer: boolean) => {
    setForm((f) => {
      if (raw === '') {
        // priorAggregateInGroup is non-nullable — fall back to 0; the rest are nullable.
        return { ...f, [key]: key === 'priorAggregateInGroup' ? 0 : null };
      }
      const n = Number(raw);
      const value = Number.isFinite(n) ? (integer ? Math.floor(n) : n) : f[key];
      return { ...f, [key]: value };
    });
  };

  const save = () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    const op =
      isEdit && initial?.id != null
        ? financialPositionApi.updateInheritance(profileId, initial.id, form)
        : financialPositionApi.createInheritance(profileId, form);
    op
      .then(() => {
        if (!isEdit) setForm(blankDraft());
        onSaved();
      })
      .catch(() => setError('Could not save the inheritance.'))
      .finally(() => setSaving(false));
  };

  return (
    <div data-testid={testidPrefix} className="flex flex-col gap-[12px]">
      <div className="flex flex-wrap gap-x-md gap-y-[10px]">
        <label className="flex flex-col gap-xs text-[13px] text-grey-04">
          Source relationship
          <input
            type="text"
            data-testid={`${testidPrefix}-sourceRelationship`}
            value={form.sourceRelationship ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, sourceRelationship: e.target.value === '' ? null : e.target.value }))
            }
            className="w-[200px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px] text-body-03 text-black"
          />
        </label>
        <label className="flex flex-col gap-xs text-[13px] text-grey-04">
          CAT group
          <select
            data-testid={`${testidPrefix}-catGroup`}
            value={form.catGroup}
            onChange={(e) => setForm((f) => ({ ...f, catGroup: e.target.value as CatGroup }))}
            className="w-[260px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px] text-body-03 text-black"
          >
            {CAT_GROUPS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        {NUMBER_FIELDS.map((nf) => (
          <label key={nf.key} className="flex flex-col gap-xs text-[13px] text-grey-04">
            {nf.label}
            <input
              type="number"
              data-testid={`${testidPrefix}-${nf.key}`}
              value={form[nf.key] ?? ''}
              onChange={(e) => setNumber(nf.key, e.target.value, nf.integer === true)}
              className="w-[160px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-[5px] text-body-03 text-black"
            />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-lg gap-y-[10px]">
        {RELIEF_FIELDS.map((rf) => (
          <label key={rf.key} className="flex items-center gap-sm text-body-03 text-black">
            <input
              type="checkbox"
              data-testid={`${testidPrefix}-${rf.key}`}
              checked={form[rf.key]}
              onChange={(e) => setForm((f) => ({ ...f, [rf.key]: e.target.checked }))}
            />
            {rf.label}
          </label>
        ))}
      </div>
      {error && <Alert variant="error">{error}</Alert>}
      <div className="flex justify-end gap-[12px]">
        {onCancel && (
          <button
            type="button"
            data-testid={`${testidPrefix}-cancel`}
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-[#E1E1E1] px-[12px] py-[6px] text-body-03 text-grey-04 disabled:opacity-60"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          data-testid={`${testidPrefix}-save`}
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-purple-primary px-[12px] py-[6px] text-body-03 text-white disabled:opacity-60"
        >
          {isEdit ? 'Save' : 'Add'}
        </button>
      </div>
    </div>
  );
}

/** Read-only corporation-tax table derived from captured company P&Ls. */
function CorporationTaxSection({ rows }: { rows: CorporationTax[] }) {
  return (
    <div className="flex flex-col w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] p-lg gap-md">
      <div className="text-black text-body-01 font-medium leading-normal font-sans">
        Corporation tax
      </div>
      {rows.length === 0 ? (
        <div data-testid="corporation-tax-empty" className="text-body-03 text-grey-04">
          No company P&L captured for this relationship.
        </div>
      ) : (
        <table data-testid="corporation-tax-table" className="w-full border-collapse text-body-03">
          <thead>
            <tr className="text-left text-grey-04">
              <th className="py-sm pr-sm font-medium">Interest type</th>
              <th className="py-sm px-sm font-medium">Trading</th>
              <th className="py-sm px-sm font-medium">Passive</th>
              <th className="py-sm px-sm font-medium">Corporation tax</th>
              <th className="py-sm px-sm font-medium">Close-company surcharge</th>
              <th className="py-sm pl-sm font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.businessInterestId}
                className="border-t border-[#E1E1E1]"
                data-testid={`corporation-tax-row-${row.businessInterestId}`}
              >
                <td className="py-[10px] pr-sm text-black">{row.interestType}</td>
                <td className="py-[10px] px-sm text-grey-04">{fmtMoney(row.tradingIncome)}</td>
                <td className="py-[10px] px-sm text-grey-04">{fmtMoney(row.passiveIncome)}</td>
                <td className="py-[10px] px-sm text-grey-04">{fmtMoney(row.corporationTax)}</td>
                <td className="py-[10px] px-sm text-grey-04">
                  {fmtMoney(row.closeCompanySurcharge)}
                </td>
                <td className="py-[10px] pl-sm text-black">{fmtMoney(row.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
